import type { ClientTheme } from "@/app/lib/themes"

interface SegmentedControlProps {
  value: string
  onChange: (next: string) => void
  options: { label: string; value: string }[]
  /** Accessible name for the button group (announced by screen readers). */
  ariaLabel?: string
  /** Optional per-option identity colors (option value → hex). When set, each
   *  segment carries a small color dot — solid on the active segment, faded on
   *  inactive ones — and the active segment's label/border take the color.
   *  Used by the Leasehold|Mineral kind tabs (theme.kindAccents). */
  accents?: Record<string, string>
  theme: ClientTheme
}

/**
 * Small tri-state (or n-state) toggle for filters like Has Well: All/Yes/No.
 * Every state is visible at a glance and one click away — nicer than a
 * dropdown for 2–3 fixed options.
 */
export function SegmentedControl({
  value,
  onChange,
  options,
  ariaLabel,
  accents,
  theme,
}: SegmentedControlProps) {
  return (
    <div
      className="inline-flex rounded-lg"
      style={{
        padding: "2px",
        gap: "2px",
        backgroundColor: theme.bgSurface2,
        border: `1px solid ${theme.borderColor}`,
      }}
      role="group"
      aria-label={ariaLabel}
    >
      {options.map((opt) => {
        const active = value === opt.value
        const accent = accents?.[opt.value]
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            className="rounded-md"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              fontFamily: "var(--font-ui)",
              fontSize: "12px",
              fontWeight: active ? 600 : 500,
              padding: "5px 10px",
              color: active ? (accent ?? theme.textPrimary) : theme.textMuted,
              backgroundColor: active ? theme.bgSurface : "transparent",
              border: `1px solid ${active ? (accent ? `${accent}66` : theme.borderColor) : "transparent"}`,
              cursor: "pointer",
              whiteSpace: "nowrap",
              transition: "background-color 120ms ease, color 120ms ease, border-color 120ms ease",
            }}
          >
            {accent && (
              <span
                aria-hidden
                className="rounded-full"
                style={{
                  width: "6px",
                  height: "6px",
                  flexShrink: 0,
                  backgroundColor: accent,
                  opacity: active ? 1 : 0.45,
                  transition: "opacity 120ms ease",
                }}
              />
            )}
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}
